import React, { useEffect, useRef, useState } from 'react';
import Chart, { useChart } from 'src/components/chart';
import { useTheme } from '@mui/material/styles';
import { useSelector } from 'react-redux';
import { useLocales } from 'src/locales';
import { useCurrentWorkspace } from 'src/context/reducers/app-settings';

type Props = {
  series: {
    name: string;
    type: string;
    data: number[];
  }[];
  labels: string[];
};

export default function ChartMixed({ series, labels }: Props) {
  const { t } = useLocales();
  const theme = useTheme();
  const filterStore = useSelector((state: any) => state.filters);
  const chartRef = useRef<any>(null);
  const [hiddenSeries, setHiddenSeries] = useState<string[]>([t('dashboard.global.blended_cpc'), t('dashboard.global.blended_cpl')]);
  const [visibleSeriesData, setVisibleSeriesData] = useState(series);
  const workspace = useCurrentWorkspace()

  useEffect(() => {
    const updatedSeries = series.map(serie => ({
      ...serie,
      show: !hiddenSeries.includes(serie.name)
    }));
    setVisibleSeriesData(updatedSeries);

    if (chartRef.current && chartRef.current.chart) {
      const chart = chartRef.current.chart;
      hiddenSeries.forEach(seriesName => {
        chart.hideSeries(seriesName);
      });
    }
  }, [series, hiddenSeries]);

  useEffect(() => {
    setHiddenSeries([t('dashboard.global.blended_cpc'), t('dashboard.global.blended_cpl')]);
  }, [t]);

  const toggleSeries = (seriesName: string) => {
    setHiddenSeries(prevHiddenSeries => {
      const isHidden = prevHiddenSeries.includes(seriesName);
      if (chartRef.current && chartRef.current.chart) {
        const chart = chartRef.current.chart;
        if (isHidden) {
          chart.showSeries(seriesName);
        } else {
          chart.hideSeries(seriesName);
        }
      }
      return isHidden ? prevHiddenSeries.filter(name => name !== seriesName) : [...prevHiddenSeries, seriesName];
    });
  };

  enum ColorSchema {
    PRIMARY = 'primary',
    SECONDARY = 'secondary',
    TERTIARY = 'tertiary',
    ERROR = 'error',
    WARNING = 'warning',
    INFO = 'info',
    SUCCESS = 'success'
  }

  const formatCurrency = (number: any, options: any, isAdCost = false) => {
    const lang: any = localStorage.getItem('i18nextLng');
    const currencySymbol = workspace?.currency === "usd" ? '$' : '€';

    let maximumFractionDigits = 2; // Valeur par défaut
    if (number >= 1000000) {
      maximumFractionDigits = 1; // Moins de décimales pour des millions
    } else if (number >= 10000) {
      maximumFractionDigits = 1; // Moins de décimales pour des milliers
    } else if (number < 1) {
      maximumFractionDigits = 4; // Plus de décimales pour des valeurs très petites
    }

    options = { ...options, maximumFractionDigits }; // Appliquer les décimales calculées dynamiquement

    let formattedNumber;
    if (isAdCost) {
      if (number >= 1000000) {
        formattedNumber = (number / 1000000).toLocaleString(lang, { maximumFractionDigits }) + 'M';
      } else if (number >= 10000) {
        formattedNumber = (number / 1000).toLocaleString(lang, { maximumFractionDigits }) + 'K';
      } else {
        formattedNumber = number.toLocaleString(lang, options);
      }
    } else if (number >= 1000000) {
      formattedNumber = (number / 1000000).toLocaleString(lang, { maximumFractionDigits }) + 'M';
    } else if (number >= 10000) {
      formattedNumber = (number / 1000).toLocaleString(lang, { maximumFractionDigits }) + 'K';
    } else {
      formattedNumber = number.toLocaleString(lang, options);
    }

    if (lang === 'fr') {
      formattedNumber = formattedNumber.replace('.', ',');
    }

    return currencySymbol + formattedNumber;
  };

  const chartOptions = useChart({
    chart: {
      stacked: series.some((serie) => serie.type === 'stacked'),
      events: {
        legendClick: function (chartContext, seriesIndex: any, config) {
          const seriesName = config.globals.seriesNames[seriesIndex];
          toggleSeries(seriesName);
          return false;
        }
      }
    },
    series:visibleSeriesData,
    plotOptions: {
      bar: {
        columnWidth: '50%',
        borderRadius: 0,
      },
    },
    labels: labels,
    fill: {
      colors: [theme.palette[ColorSchema.WARNING].light, "#006c9c", "#5119b7"]
    },
    colors: [theme.palette[ColorSchema.WARNING].light, "#006c9c", "#5119b7"],
    markers: {
      size: 6
    },
    xaxis: {
      // labels: {
      //   formatter: filterStore?.timestep === "date"
      //     ? (val) => {
      //       const date = new Date(val);
      //       const year = date.getUTCFullYear();
      //       const month = String(date.getUTCMonth() + 1).padStart(2, '0');
      //       const day = String(date.getUTCDate()).padStart(2, '0');
      //       return `${year}-${month}-${day}`;
      //     }
      //     : undefined,
      // },
    },
    yaxis: [
      {
        title: {
          text: t('dashboard.global.ad_cost'),
        },
        labels: {
          formatter: function (value) {
            return value !== undefined ? "$" + value.toFixed(1) : "";
          },
        },
      },
      {
        opposite: true,
        title: {
          text: t('dashboard.global.blended_leads'),
        },
        min: 0,
        labels: {
          formatter: function (value) {
            return value !== undefined ? value.toFixed(1) : "";
          },
        },
      },
      {
        opposite: true,
        title: {
          text: t('dashboard.global.blended_cpl'),
        },
        min: 0,
        labels: {
          formatter: function (value) {
            return value !== undefined ? "$" + value.toFixed(1) : "";
          },
        },
      },
      {
        title: {
          text: t('dashboard.global.blended_cpc'),
        },
        min: 0,
        labels: {
          formatter: function (value) {
            return value !== undefined ? "$" + value.toFixed(1) : "";
          },
        },
      },
    ],
    tooltip: {
      shared: true,
      intersect: false,
      y: {
        formatter: (value: number, { seriesIndex, w }) => {
          if (value === undefined) return "";
          const seriesName = w.config.series[seriesIndex].name;

          if (seriesName === t('dashboard.global.ad_cost') || seriesName === t('dashboard.global.blended_cpc') || seriesName === t('dashboard.global.blended_cpl')) {
            return formatCurrency(value, { minimumFractionDigits: 1, maximumFractionDigits: 1 });
          }

          return `${value.toFixed(2)}`;
        },
      },
    },
    grid: {
      padding: {
        top: 10,
        right: 10,
        bottom: 10,
        left: 10
      }
    },
    annotations: {
      xaxis: workspace?.name === "Demo" ?  [
    {
      x: filterStore?.timestep === 'date' ? '10/01/2023' : filterStore?.timestep === 'year_week' ? '09/18/2023' : '',
      x2:filterStore?.timestep === 'date' ? '10/16/2023' : filterStore?.timestep === 'year_week'  ? '10/02/2023' : '',
      fillColor: '#ffe5e9',
    },
        {
      x:filterStore?.timestep === 'date' ? '10/08/2023': filterStore?.timestep === 'year_week' ? '09/25/2023' : '',
      fillColor: '#ffe5e9',
      borderColor: 'transparent',
      label: {
            orientation: "horizontal",
            borderColor: "#FF4560",
            style: {
              color: "#fff",
              background: "#FF4560",
              fontSize:'16px',
            },
            text: "Before Cross Brand"
          }
    },
         {
          // in a datetime series, the x value should be a timestamp, just like it is generated below
          x:filterStore?.timestep === 'date' ?'10/16/2023': filterStore?.timestep === 'year_week' ? '10/02/2023' : '',
          strokeDashArray: 0,
          borderColor: "#775DD0",
          label: {
            borderColor: "#775DD0",
            style: {
              color: "#fff",
              background: "#775DD0",
              fontSize:'14px',
            },
            text: "Cross Brand Deployment"
          }
        },
        {
      x: filterStore?.timestep === 'date' ? '10/16/2023' : filterStore?.timestep === 'year_week' ? '10/02/2023' : '',
      x2:filterStore?.timestep === 'date' ? '10/31/2023' : filterStore?.timestep === 'year_week' ? '10/23/2023' : '',
      fillColor: '#B3F7CA',
    },
     {
      x: filterStore?.timestep === 'date' ? '10/23/2023' : filterStore?.timestep === 'year_week' ? '10/16/2023' : '' ,
      borderColor: 'transparent',
      label: {
            orientation: "horizontal",
            borderColor: "#00E396",
            style: {
              color: "#fff",
              background: "#00E396",
              fontSize:'16px',
            },
            text: "After Cross Brand"
          }
    }
  ] : []
    },
  });

  return <Chart dir="ltr" ref={chartRef} series={visibleSeriesData} options={chartOptions} sx={{ width: { xs: '100%', lg: '95%' } }} height={350} />;
}

